import {
	CategoryGroupOverviewItem,
	GroupGroupOverviewItem,
	listGroupOverviewData
} from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import {
	Breadcrumb,
	BusyIndicator,
	ContextActionBar,
	FloatingActionButton,
	getPropertySortText,
	NoResults,
	OptionsMenu,
	OptionsMenuItem,
	SearchInputForm,
	SearchPlaceholder,
	ToolbarAction,
	ToolbarRegularAction
} from "client/Components";
import {
	EMPTY_ARRAY,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	HubOverviewLayout,
	HubOverviewLayoutSectionContainerHandle
} from "../../HubOverviewLayout";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useStableImperativeCallback,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { useHubManagementDialog } from "../../Management/HubManagementDialog/HubManagementDialog";
import {
	ActionKind,
	buildActionsForTopBar
} from "client_legacy/ndaw/actions";
import { CreateHubItemDialog } from "../../CreateHubItemDialog/CreateHubItemDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	HubItemGroupHubSidebarNodeModel,
	HubItemHubSidebarNodeModel
} from "client/Api/Hub/hub";
import { HubItemCategoryNodeKind } from "client/Api/Hub/common";
import { never } from "client_legacy/ndaw/errors";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { contains } from "client/Data/searching";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	CategoryOverviewItem,
	DetailsState,
	GroupOverviewItem,
	OverviewItem,
	OverviewItemKind
} from "./List/GroupOverviewCardItem";
import { newGuid } from "client_legacy/ndaw/guid";
import { ActiveFilter, getGroupTotalNewItemsCount } from "../../common";
import { List } from "./List/List";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import {
	KnowledgeItemBreadcrumbSegment,
	KnowledgeItemClickableBreadcrumbSegment,
	useKnowledgeItemBreadcrumb,
	getSegmentTrackingId
} from "../useKnowledgeItemBreadcrumb";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type GroupOverviewProps = {
	groupId: number | null;
	hierarchy: readonly HubItemHubSidebarNodeModel[];
	onNavigateToGroup: (groupId: number | null) => void;
	hasCreatePermission: boolean;
	canOpenHubManagement: boolean;
	activeFilter: ActiveFilter | null;
	onClearActiveFilter: () => void;
	onSelectCategory: (id: number) => void;
	onHierarchyChanged: () => void;
	onBreadcrumbNavigation: (segment: KnowledgeItemClickableBreadcrumbSegment) => void;
};

export const GroupOverview = (props: GroupOverviewProps): ReactElement => {
	const {
		groupId,
		hierarchy,
		hasCreatePermission,
		canOpenHubManagement,
		activeFilter,
		onClearActiveFilter,
		onSelectCategory,
		onHierarchyChanged,
		onNavigateToGroup: onNavigateToGroupFromProps,
		onBreadcrumbNavigation
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [state, setState] = useState<State | null>(null);

	const [shouldShowNewItemDialog, setShouldShowNewItemDialog] = useState<boolean>(false);
	const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("pending");
	const [sorting, setSorting] = useState<DataSourceSortableState<OverviewItem>>({
		direction: DataSourceSortDirection.None
	});

	const [forceReload, itemsVersion] = useForceRerender();
	const [hubManagementDialog, openHubManagementDialog] = useHubManagementDialog();
	const containerRef: RefObject<HubOverviewLayoutSectionContainerHandle | null> = useRef<HubOverviewLayoutSectionContainerHandle>(null);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const alertManager: AlertManager = useAlerts();

	const onNavigateToGroup: typeof onNavigateToGroupFromProps = useStableImperativeCallback(onNavigateToGroupFromProps);

	const breadCrumb: readonly KnowledgeItemBreadcrumbSegment[] = useKnowledgeItemBreadcrumb({
		hierarchy,
		leaf: isNotNil(groupId)
			? {
				kind: "group",
				id: groupId
			}
			: undefined
	});

	const nonErroredItems = useMemo(
		(): readonly OverviewItem[] => {
			return (state?.items ?? EMPTY_ARRAY)
				.filter(
					(x) => x.state
							.status !== "error"
				);
		},
		[state?.items]
	);

	const {
		currentItems
	} = useDataSource(
		nonErroredItems,
		{
			searchTerm: searchTerm.value,
			sorting
		}
	);

	const closeNewItemDialogHandler = useCallback(
		(wasAnyCategoryCreated: boolean): void => {
			setShouldShowNewItemDialog(false);

			if (wasAnyCategoryCreated) {
				onHierarchyChanged();
			}
		},
		[onHierarchyChanged]
	);

	const createNewHandler = useCallback(
		(): void => {
			setShouldShowNewItemDialog(true);
		},
		[]
	);

	const navigateHandler = useCallback(
		(item: OverviewItem): void => {
			switch(item.kind) {
				case OverviewItemKind.Group:
					onNavigateToGroup(item.groupId);
					break;
				case OverviewItemKind.Category:
					onSelectCategory(item.categoryId);
					break;
				default:
					never(item);
			}
		},
		[onNavigateToGroup, onSelectCategory]
	);

	const serverErrorRetryHandler = useCallback(
		(): void => {
			forceReload();
		},
		[forceReload]
	);

	const itemDataLoadErrorRetryHandler = useCallback(
		(): void => {
			onHierarchyChanged();
			onNavigateToGroup(null);
		},
		[onNavigateToGroup, onHierarchyChanged]
	);

	const breadcrumbNavigationHandler = useCallback(
		(segment: KnowledgeItemBreadcrumbSegment): void => {
			switch(segment.kind) {
				case "item":
					throw new Error("Article is not clickable");
				case "category":
				case "group":
				case "overview":
					onBreadcrumbNavigation(segment);
					break;
				default:
					never(segment);
			}
		},
		[onBreadcrumbNavigation]
	);

	const defaultActions = useMemo(
		(): readonly Action[] => ([
			{
				kind: ActionKind.Regular,
				name: "manage-knowledge-item",
				title: localize("_Manage"),
				icon: "gear",
				canExecute: (x): boolean => x.canOpenHubManagement,
				execute: () => {
					openHubManagementDialog({
						initialView: "knowledgeItem.overview"
					});
				}
			}
		]),
		[openHubManagementDialog]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<ActionContext> => {
			return getNewAction(createNewHandler);
		},
		[createNewHandler]
	);

	const topBarActions = useMemo(
		(): readonly Action[] => {
			return buildActionsForTopBar<ActionContext, Action>(
				{
					defaultActions,
					mainActions: [mainAction]
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive,
			mainAction
		]
	);

	const actionContext = useMemo(
		(): ActionContext => ({
			hasCreatePermission,
			canOpenHubManagement
		}),
		[
			hasCreatePermission,
			canOpenHubManagement
		]
	);

	const fabHandler = useMemo(
		(): (() => void) | undefined => {
			if (mainAction.canExecute(actionContext)) {
				return () => mainAction.execute(actionContext);
			}

			return undefined;
		},
		[
			actionContext,
			mainAction
		]
	);

	useEffect(
		(): void => {
			if (isNil(groupId)) {
				const items: readonly OverviewItem[] = convertToInitialItems(hierarchy);

				setState({
					pageTitle: localize("_Overview"),
					items
				});
				return;
			}

			const result: TryResult<HubItemGroupHubSidebarNodeModel> = tryFindGroup(groupId, hierarchy);

			if (!result.wasSuccessful) {
				onNavigateToGroup(null);
				return;
			}

			const {
				result: group
			} = result;

			setState({
				pageTitle: group.name,
				items: convertToInitialItems(group.children)
			});
		},
		[
			groupId,
			hierarchy,
			onNavigateToGroup
		]
	);

	useEffect(
		(): (() => void) | void => {
			hasImplicitDependencyOn(itemsVersion);

			async function fetchData(signal: AbortSignal): Promise<void> {
				if (!state) {
					return;
				}

				const { items } = state;

				const { categoryIds, groupIds } = items
					.filter(
						(x) => x.state.status === "loading"
					)
					.reduce<{ categoryIds: number[]; groupIds: number[]; }>(
						(acc, x) => {
							switch (x.kind) {
								case OverviewItemKind.Category:
									acc.categoryIds
										.push(x.categoryId);
									break;
								case OverviewItemKind.Group:
									acc.groupIds
										.push(x.groupId);
									break;
								default:
									never(x);
							}

							return acc;
						},
						{ categoryIds: [], groupIds: [] }
					);

				const hasAnythingToRequest: boolean = categoryIds.length > 0
					|| groupIds.length > 0;

				if (!hasAnythingToRequest) {
					return;
				}

				setLoadingStatus("loading");

				try {
					const {
						categories,
						groups
					} = await listGroupOverviewData(
						{ categoryIds, groupIds },
						signal
					);

					const categoryDataById: Map<number, CategoryGroupOverviewItem> = fromArray(
						categories,
						x => x.id
					);

					const groupDataById: Map<number, GroupGroupOverviewItem> = fromArray(
						groups,
						x => x.id
					);

					setState(
						(x): State | null => {
							if (!x) {
								return x;
							}

							const items: readonly OverviewItem[] = x.items
								.map(
									(x): OverviewItem => {
										switch (x.kind) {
											case OverviewItemKind.Category:
												return updateCategoryData(x, categoryDataById);
											case OverviewItemKind.Group:
												return updateGroupData(x, groupDataById);
											default:
												return never(x);
										}
									}
								);

							return {
								...x,
								items
							};
						}
					);

					setLoadingStatus("ready");
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					setLoadingStatus("error");
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			groupId,
			state,
			onNavigateToGroup,
			itemsVersion
		]
	);

	const renderResult = (): ReactElement => {
		if (loadingStatus === "loading") {
			return <BusyIndicator />;
		}

		if (loadingStatus === "error") {
			return (
				<NoResults
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					height="fill"
					onRetry={serverErrorRetryHandler}
					trackingId="server-error"
				/>
			);
		}

		const isEmptyDueToErrors: boolean = isNotNil(state)
			&& state.items
				.length > 0
			&& state
				.items
				.every(
					x => x.state.status === "error"
				);

		if (isEmptyDueToErrors) {
			return isNotNil(groupId)
				? (
					<NoResults
						icon="book"
						title={localize("_NoItemsToShow")}
						message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
						height="fill"
						onRetry={itemDataLoadErrorRetryHandler}
						retryText={localize("_BackToOverview")}
						trackingId="server-error"
					/>
				)
				: (
					<NoResults
						icon="book"
						title={localize("_NoKnowledgeArticlesYet")}
						message={localize("_ThereAreNoKnowledgeArticlesYet")}
						height="fill"
						trackingId="server-error-on-overview"
					/>
				);
		}

		if (currentItems.length === 0) {
			return searchTerm.value
			? (
				<NoResults
					icon="magnifying-glass"
					title={localize("_NoSearchResults")}
					message={localize("_PleaseTrySearchingWithAnotherTerm")}
					height="fill"
					trackingId="no-search-results-found"
				/>
			)
			: (
				<NoResults
					icon="book"
					title={localize("_NoKnowledgeArticlesYet")}
					message={localize("_ThereAreNoKnowledgeArticlesYet")}
					height="fill"
					trackingId="no-results-found"
				/>
			);
		}

		return (
			<>
				<List
					items={currentItems}
					onClickItem={navigateHandler}
				/>
				<AiAssistantFabSpacing />
			</>
		);
	};

	return (
		<>
			<HubOverviewLayout trackingId="knowledge-items-group-overview">
				{isMobile && (
					<ContextActionBar
						actions={topBarActions}
						context={actionContext}
						hasBorderBottom
						trackingId="global-action-bar"
					/>
				)}

				<HubOverviewLayout.Content>
					<HubOverviewLayout.Breadcrumb>
						<Breadcrumb
							segments={breadCrumb}
							onNavigate={breadcrumbNavigationHandler}
							trackingId="breadcrumb"
							getSegmentTrackingId={getSegmentTrackingId}
						/>
					</HubOverviewLayout.Breadcrumb>
					<HubOverviewLayout.Header
						trackingId="knowledge-items-group-overview-header"
						activeFilter={activeFilter}
						onClearActiveFilter={onClearActiveFilter}
						title={state?.pageTitle ?? ""}
						actions={topBarActions}
						actionContext={actionContext}
					>
						<SearchInputForm
							value={searchTerm}
							onChange={(value, version) => setSearchTerm({ value, version })}
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Name")
									]}
								/>
							)}
							trackingId="search-input"
							height="large"
							icon="filter"
						/>
						<OptionsMenu
							title={localize("_SortBy")}
							trackingId="sort-by"
							value={sorting}
							onChange={setSorting}
							options={sortOptions}
							icon="arrow-up-arrow-down"
							buttonSize="large"
						/>
					</HubOverviewLayout.Header>
					<HubOverviewLayout.SectionContainer
						trackingId="section"
						ref={containerRef}
					>
						{renderResult()}
					</HubOverviewLayout.SectionContainer>
					{fabHandler && (
						<FloatingActionButton
							icon="plus"
							onClick={fabHandler}
							trackingId="create-knowledge-item-mobile-button"
						/>
					)}
				</HubOverviewLayout.Content>
			</HubOverviewLayout>
			{hubManagementDialog}
			{shouldShowNewItemDialog && (
				<CreateHubItemDialog
					kind={HubItemKind.KnowledgeItem}
					onClose={closeNewItemDialogHandler}
					onBack={closeNewItemDialogHandler}
				/>
			)}
		</>
	);
};
GroupOverview.displayName = "GroupOverview";

type State = {
	pageTitle: string;
	items: readonly OverviewItem[];
};

type LoadingStatus =
	| "pending"
	| "loading"
	| "error"
	| "ready";

type ActionContext = {
	hasCreatePermission: boolean;
	canOpenHubManagement: boolean;
};

type Action = ToolbarAction<ActionContext>;

const sortOptions: readonly OptionsMenuItem<DataSourceSortableState<OverviewItem>>[] = [
	{
		text: localize("_Default"),
		value: { direction: DataSourceSortDirection.None },
		valueTrackingId: "name-default"
	},
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	}
];

const useDataSource = createLocalDataSource<OverviewItem>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.name, searchTerm)
		}
	})
	.getHook();

function normalizedItemId(
	item: HubItemHubSidebarNodeModel
): string {
	switch(item.kind) {
		case HubItemCategoryNodeKind.Group:
			return `group_${item.groupId}`;
		case HubItemCategoryNodeKind.Category:
			return `category_${item.category.id}`;
		default:
			never(item);
	}
};

function convertToInitialItems(
	items: readonly HubItemHubSidebarNodeModel[]
): readonly OverviewItem[] {
	return items.map(
		(x): OverviewItem => {
			switch(x.kind) {
				case HubItemCategoryNodeKind.Group:
					return {
						kind: OverviewItemKind.Group,
						uniqueKey: normalizedItemId(x),
						groupId: x.groupId,
						name: x.name,
						newItemsCount: getGroupTotalNewItemsCount(x),
						state: {
							status: "loading"
						}
					};
				case HubItemCategoryNodeKind.Category:
					return {
						kind: OverviewItemKind.Category,
						uniqueKey: normalizedItemId(x),
						categoryId: x.category.id,
						name: x.category.name,
						newItemsCount: x.newItemsCount,
						state: {
							status: "loading"
						}
					};
				default:
					return never(x);
			}
		}
	);
}

function updateStateWithDetails<
	TItem extends { name: string; state: DetailsState<TDetails>; },
	TDetails extends { name: string; }
>(item: TItem, details: TDetails | nil): TItem {
	if (isNil(details)) {
		if (item.state.status !== "loading") {
			return item;
		}

		return {
			...item,
			state: { status: "error" }
		};
	}

	return {
		...item,
		name: details.name,
		state: {
			status: "ready",
			details
		}
	};
}

function updateCategoryData(
	item: CategoryOverviewItem,
	categoryDataById: Map<number, CategoryGroupOverviewItem>
): CategoryOverviewItem {
	const details: CategoryGroupOverviewItem | undefined = categoryDataById.get(item.categoryId);
	return updateStateWithDetails(item, details);
}

function updateGroupData(
	item: GroupOverviewItem,
	groupDataById: Map<number, GroupGroupOverviewItem>
): GroupOverviewItem {
	const details: GroupGroupOverviewItem | undefined = groupDataById.get(item.groupId);
	return updateStateWithDetails(item, details);
}

function getNewAction(
	handler: () => void
): ToolbarRegularAction<ActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "create-knowledge-item",
		title: localize("_New"),
		icon: "plus",
		isPrimary: true,
		canExecute: (x): boolean => x.hasCreatePermission,
		execute: (): void => {
			handler();
		}
	};
}

function tryFindGroup(
	groupId: number,
	hierarchy: readonly HubItemHubSidebarNodeModel[]
): TryResult<HubItemGroupHubSidebarNodeModel> {
	for (const item of hierarchy) {
		if (item.kind === HubItemCategoryNodeKind.Group) {
			if (item.groupId === groupId) {
				return {
					wasSuccessful: true,
					result: item
				};
			}

			const childResult = tryFindGroup(groupId, item.children);
			if (childResult.wasSuccessful) {
				return childResult;
			}
		}
	}

	return { wasSuccessful: false };
}